import React, {useState, useEffect} from 'react';
import styled from "@emotion/styled";
import {InputAdornment, Box, TextField} from '@mui/material';
import ExpandableSelect from "./SelectLeave";
import DateDialog from "./DateDialog";
import CalenderIcon from "../../../../../assets/icons/calendar-dash.svg";
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {leaveRequest, getSelfLeaveBalance} from "../../../../../services/leave";
import CheckboxField from '../../../../../component/CheckboxField';
import { useNotify } from '../../../../../component/NotificationProvider';
import { tokens } from "../../../../../theme";
import { ConsequenceRow } from "../../../../../component/Sheet";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import InlineMessage from "../../../../../component/InlineMessage";
import { useSelfEmployee, useOrgDepartments } from "../../../../../services/useOrgData";
import { getLeaveCalendar } from "../../../../../services/leave";
import { clashNote, approverLine } from "./leaveClash";
dayjs.extend(isBetween);

const TextBox = styled(Box)({
    width: "100%",
    fontWeight: 500,
    fontSize: "1rem",
    lineHeight:1.7,
    marginTop: "1.5rem",
    marginBottom: "0.5rem",
  });

const InputBoxStyles ={
    backgroundColor:"#F1E4E6",
    borderRadius: "6px",
    fontSize:"1rem",
    lineHeight:1.7,
    color:tokens.pendingText
}

const leaveRecordStyles ={
  fontSize:"0.875rem", lineHeight: 1.6
}

/**
 * @param onSubmitted  optional; called after a request is actually created.
 *   `onClose` fires on cancel too, so a caller that needs to refresh has no way
 *   to tell "they sent it" from "they changed their mind" without this.
 */
export default function ApplyLeave({ open, onClose, onSubmitted }) {

  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [halfDate, setHalfDate] = useState(null);
  const [halfLeaveList,setHalfLeaveList] = useState([]); //array containing half days
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [halfDialogOpen, setHalfDialogOpen] = useState(false);
  const [isHalfDay,setIsHalfDay] = useState(false);
  const [reason, setReason] = useState('');
  const [days, setDays] = useState([]); //array to hold days object
  const [halfCount,setHalfCount] = useState('');
  const [fullCount, setFullCount] = useState('');
  // Needed for the consequence line: the book wants the balance *after* this
  // request, which nobody can work out in their head from a total and a range.
  const [balance, setBalance] = useState(null);
  const { data: self } = useSelfEmployee();
  // Employee.department is a String holding a department id, so `.name` on it
  // is always undefined — the clash line said "Nobody else is away" where it
  // meant "Nobody else in Engineering is away", every time, for everybody.
  const { data: departments } = useOrgDepartments();
  const myTeam =
    (departments || []).find((d) => String(d?._id) === String(self?.department))?.name || null;
  // Who else is off. Loaded once; a failure costs the clash line, not the form.
  const [calendar, setCalendar] = useState([]);
  const clash = clashNote(
    calendar,
    startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
    endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
    self?._id,
    myTeam
  );
  useEffect(() => {
    let active = true;
    getLeaveCalendar({ status: "approved" })
      .then((r) => { if (active) setCalendar(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    let live = true;
    (async () => {
      try {
        const res = await getSelfLeaveBalance();
        if (live) setBalance(typeof res?.data === "number" ? res.data : res?.data?.total ?? null);
      } catch {
        // an unknown balance is shown as unknown, not as zero
        if (live) setBalance(null);
      }
    })();
    return () => { live = false; };
  }, [open]);

  const requestedDays = (Number(fullCount) || 0) + (Number(halfCount) || 0) * 0.5;

  const leaveForm = {
    leaveType: leaveType,
    days: days,
    reason: reason
    }

    const handleClose = () => {
      setLeaveType('');
      setStartDate(null);
      setEndDate(null);
      setIsHalfDay(false);
      setHalfDate(null);
      setReason('');
      setDays([]);
      setHalfLeaveList([]);
      setFullCount('');
      setHalfCount('');
      onClose();
    }; 
    
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!leaveType) { notify.error("Choose a leave type"); return; }
    if (!days.length) { notify.error("Select a start and end date"); return; }
    if (!reason.trim()) { notify.error("Please provide a reason"); return; }
    setSubmitting(true);
    try {
      const res = await leaveRequest(leaveForm);
      if (res?.success === false) throw new Error(res?.message || "Could not apply for leave");
      notify.success("Leave request submitted");
      handleClose();
      if (onSubmitted) onSubmitted();
    } catch (err) {
      notify.error(err?.message || "Could not apply for leave");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeave = (value) => {setLeaveType(value)}; 

  const handleStartDateSelect = (date) => {
    setStartDate(date);
    setStartDialogOpen(false); 
  };

  const handleEndDateSelect = (date) => {
    setEndDate(date);
    setEndDialogOpen(false);
  };

  const generateDaysArray = () => {
    const daysArray = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    while (currentDate <= end) {
      const unixTimestampUTC = currentDate.getTime();
      daysArray.push({ date: unixTimestampUTC, window: 'full-day'});
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    setDays(daysArray);
  };
  

  useEffect(() => {
    if (startDate && endDate) {generateDaysArray();}
  }, [startDate, endDate]);

  useEffect(() => {
    let full = 0;
    let half = 0;
    days.forEach((day) => {
      if (day.window === 'full-day') {
        full++;
      } else {
        half++;
      }
    });
    setFullCount(full);
    setHalfCount(half);
  }, [days]);

  const handleHalfDateSelect = (date) => {
    setHalfDate(date);
    setHalfDialogOpen(false);
  };

  const handleHalfSelected = () =>{
    const check = new Date(halfDate);
    const halfDateTimestamp = check.getTime();
    console.log(halfDateTimestamp);
    const updatedDays = days.map(day => {
      if (day.date === halfDateTimestamp) {
          return {...day, window:"first-half"};
      }
      return day;
  });
  setDays(updatedDays);
  }

  useEffect(() => {
    if (halfDate && !halfLeaveList.includes(halfDate)) {
      handleHalfSelected();
      setHalfLeaveList(prevHalfLeaveList => [...prevHalfLeaveList, halfDate]);
    }
  }, [halfDate, halfLeaveList]);

  useEffect(() => {
    if (isHalfDay) {
      setHalfDate(null); 
    }
  }, [isHalfDay]);

  useEffect(() => {
    setEndDate(null);
    setIsHalfDay(false);
    setHalfDate(null);
    setHalfLeaveList([]);
  }, [startDate]);
  
  useEffect(() => {
    setIsHalfDay(false);
    setHalfDate(null);
    setHalfLeaveList([]);
  }, [endDate]);

  return (
    /*
      A modal over the page, 680px, per the portal book — not the 460px side
      sheet. The sheet was right when this was one question; it now carries a
      clash line, two consequence rows and a note, and at 460 those stop reading
      as one decision and start reading as a form.
    */
    <Modal open={open} onClose={handleClose} width="42.5rem">
      <ModalHead
        title="Request leave"
        subtitle={approverLine(self?.reportingManager)}
        onClose={handleClose}
      />
      <ModalBody>
      <Box>
          {/*
            Who else is off across these dates. Said before the request is sent,
            because a clash that is known early gets moved rather than refused —
            and the person asking is the only one who cannot see the team's
            calendar.
          */}
          {clash && (
            <InlineMessage tone={clash.tone} sx={{ mb: "1.25rem" }}>
              {clash.text}
            </InlineMessage>
          )}

          <Box>
             <TextBox>Choose Leave Type</TextBox>
             <ExpandableSelect onLeaveSelect={handleLeave}/>
          </Box>

          <Box>
             <TextBox>Start Date</TextBox>
             <TextField
              placeholder="YYYY-MM-DD"
              value={startDate || ''}
              onClick={() => setStartDialogOpen(true)}
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={CalenderIcon} alt="Calendar" />
                  </InputAdornment>
                ),
              }}
              sx={{"& .MuiInputBase-root":InputBoxStyles}} 
            />
            <DateDialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)} onSelect={handleStartDateSelect} />
          </Box>

          <Box>
             <TextBox>End Date</TextBox>
             <TextField
              placeholder="YYYY-MM-DD"
              value={endDate || ''}
              onClick={() => setEndDialogOpen(true)}
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={CalenderIcon} alt="Calendar" />
                  </InputAdornment>
                ),
              }}
              sx={{"& .MuiInputBase-root":InputBoxStyles}} 
            />
            <DateDialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)} onSelect={handleEndDateSelect} currentStartDate={startDate}/>
          </Box>

          <Box>
             <TextBox>Half Day</TextBox>
             <CheckboxField
                   label="Is It half leave ?"
                   checked={isHalfDay}
                   onChange={setIsHalfDay}
                   labelStyle={{fontSize: "0.875rem", lineHeight: 1.6, color: tokens.pendingText }}
                   checkboxStyle={{fontSize:"1rem"}}
             />
          </Box>

          {isHalfDay && (<Box>
             <TextBox>Half Day Date</TextBox>
             <TextField
              placeholder="YYYY-MM-DD"
              value={halfDate || ''}
              onClick={() => setHalfDialogOpen(true)}
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={CalenderIcon} alt="Calendar" />
                  </InputAdornment>
                ),
              }}
              sx={{"& .MuiInputBase-root":InputBoxStyles}} 
            />
            <DateDialog open={halfDialogOpen} onClose={() => setHalfDialogOpen(false)} onSelect={handleHalfDateSelect} currentStartDate={startDate} currentEndDate={endDate} isHalfDay={isHalfDay}/>
          </Box>
          )}

         <Box sx={{width:"100%", marginTop: "1rem"}}>
          {startDate && endDate && fullCount!==0 && (
            <Box sx={{width:"100%", display:"flex", justifyContent:"space-between", marginTop: "0.75rem"}}>
               <Box sx={{...leaveRecordStyles, flex:1}}>
                 {dayjs(startDate).format("DD-MM-YYYY")} - {dayjs(endDate).format("DD-MM-YYYY")}
               </Box>
            <Box sx={{...leaveRecordStyles,}}>Full Leave</Box>
               <Box sx={{...leaveRecordStyles,flex:1, textAlign:"center"}}>
                 {fullCount} leave
               </Box>
            </Box>
          )
          }
          {isHalfDay && halfDate && (
             <Box sx={{width:"100%", display:"flex", justifyContent:"space-between"}}>
             <Box sx={{...leaveRecordStyles, flex:1}}>
                {halfLeaveList.map(date => dayjs(date).format("DD-MM-YYYY")).join(', ')}
             </Box>
             <Box sx={{...leaveRecordStyles}}>Half Leave</Box>
             <Box sx={{...leaveRecordStyles, flex:1, textAlign:"center"}}>
               {halfCount*0.5} leave
             </Box>
             </Box>
          )}
          </Box>

          <Box>
             <TextBox>Reason</TextBox>
             <TextField 
                id="reason" 
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                sx={{"& .MuiInputBase-root":InputBoxStyles,
                      "& .MuiOutlinedInput-notchedOutline":{
                          border:"none"}
              }} 
             />
          </Box>

      </Box>

        {/* What this costs, stated before it is sent. */}
        {requestedDays > 0 && (
          <Box sx={{ mt: "1.5rem", pt: "1.25rem", borderTop: `1px solid ${tokens.border}` }}>
            <ConsequenceRow label="Working days requested" value={requestedDays} />
            <ConsequenceRow
              label="Balance after approval"
              value={balance == null ? "—" : `${Math.round((balance - requestedDays) * 10) / 10} days`}
              strong
            />
          </Box>
        )}
      </ModalBody>

      <ModalFoot note="You will get an email when your manager responds.">
        <BtnSecondary onClick={handleClose} sx={{ borderRadius: "8px" }}>Discard</BtnSecondary>
        <BtnPrimary
          onClick={handleSubmit}
          disabled={submitting || !leaveType || !requestedDays}
          sx={{ borderRadius: "8px" }}
        >
          {submitting ? "Sending…" : "Send request"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
}
